import {
  Table,
  Button,
  Row,
  Col,
  Form,
  Pagination,
} from "react-bootstrap";
import { Link } from "react-router-dom";
import Message from "../../components/Message";
import Loader from "../../components/Loader";
import {
  useGetOpeningStoreStocksQuery,
  useDeleteOpeningStoreStockMutation,
} from "../../slices/openingStoreStocksApiSlice";
import { useGetStoreSuppliesQuery } from "../../slices/storeSuppliesApiSlice"; // For Pqty and Pcost
import { toast } from "react-toastify";
import { useState } from "react";

const OpeningStoreStockListScreen = () => {
  // State for filtering, pagination, and date range
  const [filter, setFilter] = useState("");
  const [pageNumber, setPageNumber] = useState(1);
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");

  // Fetch opening storeStocks and storeSupplies data
  const { data: openingStoreStockData, isLoading, error, refetch } =
    useGetOpeningStoreStocksQuery({pageNumber});
  const { data: storeSuppliesData } = useGetStoreSuppliesQuery(); // StoreSupplies data for Pqty and Pcost

  // Mutation for deleting opening storeStocks
  const [deleteOpeningStoreStock, { isLoading: loadingDelete }] =
    useDeleteOpeningStoreStockMutation();

  // Handle deletion
  const deleteHandler = async (id) => {
    if (window.confirm("Are you sure you want to delete this opening storeStock?")) {
      try {
        await deleteOpeningStoreStock(id).unwrap();
        toast.success("Opening storeStock deleted successfully!");
        refetch();
      } catch (err) {
        toast.error(err?.data?.message || "Failed to delete opening storeStock.");
      }
    }
  };

  // Filter opening storeStocks based on product name, note, and date range
  const filterOpeningStoreStocks = () => {
    if (!openingStoreStockData?.openingStoreStocks) return [];
    return openingStoreStockData.openingStoreStocks.filter((openingStoreStock) => {
      const openingStoreStockDate = new Date(openingStoreStock.date);
      const isWithinDateRange =
        (!startDate || openingStoreStockDate >= new Date(startDate)) &&
        (!endDate || openingStoreStockDate <= new Date(endDate));
      return (
        isWithinDateRange &&
        (openingStoreStock.note?.toLowerCase().includes(filter.toLowerCase()) ||
          openingStoreStock.openingStoreStockItems.some((item) =>
            item.name.toLowerCase().includes(filter.toLowerCase())
          ))
      );
    });
  };

  // Helper functions for Pqty, Pcost, and AverageCost
  const calculatePqty = (productName) => {
    return storeSuppliesData?.storeSupplies
      ?.reduce((totalQty, storeSupply) => {
        const productItems = storeSupply.storeSupplyItems.filter(
          (item) => item.name === productName
        );
        const productQty = productItems.reduce((qty, item) => qty + item.qty, 0);
        return totalQty + productQty;
      }, 0) || 0;
  };

  const calculatePcost = (productName) => {
    return storeSuppliesData?.storeSupplies
      ?.reduce((totalCost, storeSupply) => {
        const productItems = storeSupply.storeSupplyItems.filter(
          (item) => item.name === productName
        );
        const productCost = productItems.reduce(
          (cost, item) => cost + item.itemCost,
          0
        );
        return totalCost + productCost;
      }, 0) || 0;
  };

  const calculateAverageCost = (itemCost, qty, productName) => {
    const Pcost = calculatePcost(productName);
    const Pqty = calculatePqty(productName);
    const totalQty = qty + Pqty;
    const totalCost = itemCost + Pcost;

    return totalQty > 0 ? totalCost / totalQty : 0; // Avoid division by zero
  };

  // Handle pagination changes
  const paginationHandler = (page) => {
    setPageNumber(page);
  };
  
    const calculateTotalRevenue = () => {
  if (!openingStoreStockData?.openingStoreStocks) return 0;
  return openingStoreStockData.openingStoreStocks.reduce(
    (total, openingStoreStock) =>
      total +
      openingStoreStock.openingStoreStockItems.reduce(
        (subTotal, item) => subTotal + item.qty * item.price,
        0
      ),
    0
  ).toFixed(2);
};
const calculateTotalCost = () => {
  if (!openingStoreStockData?.openingStoreStocks) return 0;
  return openingStoreStockData.openingStoreStocks.reduce(
    (total, openingStoreStock) =>
      total +
      openingStoreStock.openingStoreStockItems.reduce(
        (subTotal, item) => subTotal + item.itemCost,
        0
      ),
    0
  ).toFixed(2);
};
const calculateTotalProfit = () => {
  if (!openingStoreStockData?.openingStoreStocks) return 0;
  return openingStoreStockData.openingStoreStocks.reduce(
    (total, openingStoreStock) =>
      total +
      openingStoreStock.openingStoreStockItems.reduce(
        (subTotal, item) => subTotal + item.profit,
        0
      ),
    0
  ).toFixed(2);
};


  return (
    <>
      {/* Header */}
      <Row className="align-items-center mb-4">
        <Col>
          <h1>Opening MiniStocks</h1>
          <Link to='/storeAdmin/openingstocksearchReplace' className='btn btn-light my-3'>
           Search
          </Link>
        </Col>
      </Row>

      {/* Filters */}
      <Row className="mb-3">
        <Col md={3}>
          <Form.Control
            type="date"
            value={startDate}
            onChange={(e) => setStartDate(e.target.value)}
          />
        </Col>
        <Col md={3}>
          <Form.Control
            type="date"
            value={endDate}
            onChange={(e) => setEndDate(e.target.value)}
          />
        </Col>
        <Col md={4}>
          <Form.Control
            type="text"
            placeholder="Filter by product name or note"
            value={filter}
            onChange={(e) => setFilter(e.target.value)}
          />
        </Col>
        <Col md={2} className="text-end">
          <Button onClick={() => refetch()}>Reset</Button>
        </Col>
      </Row>

      {/* Totals Display */}
      <Row className="mb-3">
        <Col md={4}>
          <h4>Total Revenue: ${filterOpeningStoreStocks()
            .reduce(
              (total, openingStoreStock) =>
                total +
                openingStoreStock.openingStoreStockItems.reduce(
                  (subTotal, item) => subTotal + item.qty * item.price,
                  0
                ),
              0
            )
            .toFixed(2)}</h4>
        </Col>
        <Col md={4}>
          <h4>Total Cost: ${filterOpeningStoreStocks()
            .reduce(
              (total, openingStoreStock) =>
                total +
                openingStoreStock.openingStoreStockItems.reduce(
                  (subTotal, item) => subTotal + item.itemCost,
                  0
                ),
              0
            )
            .toFixed(2)}</h4>
        </Col>
        <Col md={4} className="text-end">
          <h4>Total Profit: ${filterOpeningStoreStocks()
            .reduce(
              (total, openingStoreStock) =>
                total +
                openingStoreStock.openingStoreStockItems.reduce(
                  (subTotal, item) => subTotal + item.profit,
                  0
                ),
              0
            )
            .toFixed(2)}</h4>
        </Col>
      </Row>
      <Row className="mb-3">
     <Col md={4}>
    <h4>Total Revenue: ${calculateTotalRevenue()}</h4>
    </Col>
    <Col md={4}>
    <h4>Total Cost: ${calculateTotalCost()}</h4>
    </Col>
    <Col md={4} className="text-end">
    <h4>Total Profit: ${calculateTotalProfit()}</h4>
      </Col>
     </Row>

      {/* Table */}
      {loadingDelete && <Loader />}
      {isLoading ? (
        <Loader />
      ) : error ? (
        <Message variant="danger">
          {error?.data?.message || "An error occurred"}
        </Message>
      ) : (
        <>
        <Table striped bordered hover responsive className="table-sm">
          <thead>
            <tr>
              <th>DATE</th>
              <th>PRODUCT NAME</th>
              <th>QTY</th>
              <th>PQTY</th>
              <th>REV</th>
              <th>COST</th>
              <th>PCOST</th>
              <th>AVG COST</th>
              <th>PROFIT</th>
              <th>NOTE</th>
              <th>IMAGE</th>
              <th>ACTIONS</th>
            </tr>
          </thead>
          <tbody>
            {filterOpeningStoreStocks().map((openingStoreStock) =>
              openingStoreStock.openingStoreStockItems.map((item) => {
                const Pqty = calculatePqty(item.name);
                const Pcost = calculatePcost(item.name);
                const averageCost = calculateAverageCost(
                  item.itemCost,
                  item.qty,
                  item.name
                );

                return (
                  <tr key={`${openingStoreStock._id}-${item.product}`}>
                    <td>{new Date(openingStoreStock.date).toLocaleDateString()}</td>
                    <td>{item.name}</td>
                    <td>{item.qty}</td>
                    <td>{Pqty}</td>
                    <td>${(item.qty * item.price).toFixed(2)}</td>
                    <td>${item.itemCost.toFixed(2)}</td>
                    <td>${Pcost.toFixed(2)}</td>
                    <td>${averageCost.toFixed(2)}</td>
                    <td>${item.profit.toFixed(2)}</td>
                    <td>{openingStoreStock.note || "N/A"}</td>
                    <td>
                      {item.images && (
                        <img
                          src={item.images[0]}
                          alt="Thumbnail"
                          className="thumbnail-img me-2"
                          style={{
                            width: "40px",
                            height: "40px",
                            objectFit: "cover",
                            borderRadius: "5px",
                          }}
                        />
                      )}
                    </td>
                    <td>
                      <Button
                        as={Link}
                        to={`/openingStoreStock/${openingStoreStock._id}`}
                        variant="light"
                        className="btn-sm"
                      >
                        Details
                      </Button>
                      <Button
                          as={Link}
                          to={`/openingStoreStock/${openingStoreStock._id}/edit`}
                          variant="light"
                          className="btn-sm mx-2"
                          >
                          Edit
                        </Button>

                      <Button
                        variant="danger"
                        className="btn-sm mx-2"
                        onClick={() => deleteHandler(openingStoreStock._id)}
                      >
                        Delete
                      </Button>
                    </td>
                  </tr>
                );
              })
            )}
          </tbody>
        </Table>
        {/* Pagination */}
          <Pagination>
            {[...Array(openingStoreStockData.pages).keys()].map((x) => (
              <Pagination.Item
                key={x + 1}
                active={x + 1 === pageNumber}
                onClick={() => paginationHandler(x + 1)}
              >
                {x + 1}
              </Pagination.Item>
            ))}
          </Pagination>
        </>
      )}
    </>
  );
};

export default OpeningStoreStockListScreen;